import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const fmt = (s) => (s ? new Date(s).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "medium" }) : "—");

function SecretForm({ data, onChange }) {
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (value) => {
    setBusy(true);
    try { await api.post("/connections/yousign/webhook-secret", { secret: value }); toast.success(value ? "Secret enregistré : notifications vérifiées" : "Secret supprimé"); setSecret(""); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-5 pt-5 border-t border-norea-border">
      <div className="text-xs font-medium text-norea-textMuted uppercase tracking-wider mb-1">Notifications (webhook)</div>
      <div className="text-xs text-norea-textMuted mb-2">Adresse à déclarer dans Yousign : <span className="font-mono text-norea-text select-all" data-testid="yousign-webhook-url">{data?.webhook_url}</span></div>
      <div className="flex flex-wrap gap-2 items-center">
        <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={data?.webhook_secret_masked || "Secret du webhook Yousign"}
          className="flex-1 min-w-[240px] px-3 py-2 border border-norea-border rounded-md text-sm font-mono outline-none focus:border-norea-primary" data-testid="yousign-webhook-secret-input" />
        <button onClick={() => save(secret.trim())} disabled={busy || !secret.trim()} className="btn-secondary disabled:opacity-50" data-testid="yousign-webhook-secret-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer le secret"}</button>
        {data?.webhook_secret_masked && <button onClick={() => window.confirm("Supprimer le secret ? Les notifications seront acceptées sans vérification.") && save("")} className="text-xs text-norea-textMuted hover:text-status-erreurText" data-testid="yousign-webhook-secret-remove">Supprimer</button>}
      </div>
      <div className="mt-2 text-xs" data-testid="yousign-webhook-verif">
        {data?.webhook_verified
          ? <span className="inline-flex items-center gap-1 text-status-completText"><ShieldCheck className="w-3.5 h-3.5" /> Signature de chaque notification vérifiée{data.webhook_secret_masked ? ` (secret ${data.webhook_secret_masked})` : ""}</span>
          : <span className="inline-flex items-center gap-1 text-amber-700"><ShieldAlert className="w-3.5 h-3.5" /> Aucun secret : notifications acceptées mais « non vérifiées »</span>}
      </div>
    </div>
  );
}

function LogTable() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/yousign/webhook-log", { params: { limit: 50 } }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const verif = (v) => (v === true ? "vérifiée" : v === false ? "refusée" : "non vérifiée");
  return (
    <div className="mt-5 pt-5 border-t border-norea-border" data-testid="yousign-webhook-log">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-medium text-norea-textMuted uppercase tracking-wider">Journal des notifications reçues</div>
        <button onClick={load} className="text-xs text-norea-primary inline-flex items-center gap-1" data-testid="yousign-webhook-log-refresh"><RefreshCw className="w-3 h-3" /> Actualiser</button>
      </div>
      {!items ? <div className="text-xs text-norea-textMuted">Chargement…</div> : items.length === 0 ? <div className="text-xs text-norea-textMuted" data-testid="yousign-webhook-log-empty">Aucune notification reçue pour l'instant.</div> : (
        <div className="max-h-72 overflow-auto border border-norea-border rounded-md">
          <table className="w-full text-xs">
            <thead className="bg-norea-surfaceHover text-norea-textMuted sticky top-0"><tr><th className="text-left px-2 py-1.5">Date</th><th className="text-left px-2">Événement</th><th className="text-left px-2">Dossier / document</th><th className="text-left px-2">Statut</th><th className="text-left px-2">Contrôle</th><th className="text-left px-2">Résultat</th></tr></thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-t border-norea-border" data-testid={`yousign-log-row-${i.id}`}>
                  <td className="px-2 py-1.5 whitespace-nowrap">{fmt(i.at)}</td>
                  <td className="px-2 font-mono">{i.event_name}</td>
                  <td className="px-2">{i.dossier_id ? <Link to={i.sav_id ? `/sav/${i.sav_id}` : `/dossiers/${i.dossier_id}`} className="text-norea-primary hover:underline">{i.dossier_name || i.dossier_id}</Link> : "—"}{i.doc_name && <div className="text-norea-textMuted">{i.doc_name}</div>}</td>
                  <td className="px-2">{i.status_label || "—"}</td>
                  <td className={`px-2 ${i.verified === true ? "text-status-completText" : i.verified === false ? "text-status-erreurText" : "text-amber-700"}`}>{verif(i.verified)}</td>
                  <td className="px-2 text-norea-textMuted">{i.result}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function YousignWebhookSection({ data, onChange }) {
  if (!data?.configured) return null;
  return (<><SecretForm data={data} onChange={onChange} /><LogTable /></>);
}
